<template>
  <div class="settings">
    <VPBreadcrumbs :elements="[{ text: 'Whitelabel', to: parentRoute }, { text: whitelabelName }]" />
    <Form
      v-if="whitelabel"
      :form-id="formId"
      :initial-values="whitelabel"
    >
      <WhitelabelFields />

      <FormActionButtons
        show-delete
        :show-cancel="isFormDirty()"
        :show-save="isFormDirty()"
        class="form-buttons"
        @save="handleSave"
        @cancel="onCancel"
        @delete="triggerDeleteConfirmation"
      />
      <VPModal
        v-model:show="showDeleteConfirmation"
        title="Confirm Whitelabel Deletion"
        confirm-label="Delete"
        close-label="Cancel"
        size="sm"
        @confirm="confirmDelete"
        @close="closeModal"
      >
        <p class="text-center text-vp-base-900">
          Are you sure you want to delete whitelabel '{{ whitelabel.name }}'?
        </p>
      </VPModal>
    </Form>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { Form, useFormsStoreComposable } from 'vuniform';
import VPBreadcrumbs from '@/components/VPBreadcrumbs.vue';
import FormActionButtons from '@/components/form/FormActionButtons.vue';
import { useWhitelabelStore } from '@/stores/whitelabel';
import type { SaveWhitelabelConfigMutationInput, WhitelabelAttributes } from '@/generated/graphql';
import VPModal from '@/components/form/VPModal.vue';
import WhitelabelFields from './WhitelabelFields.vue';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const whitelabelStore = useWhitelabelStore();
const { formId, formsStore, formGetValues, isFormDirty } = useFormsStoreComposable('edit-whitelabel-form');
const parentRoute = { name: 'WhitelabelIndex' };

const whitelabel = computed(() => {
  return whitelabelStore.whitelabels.find(({ id }) => id == Number(route.params.whitelabelId));
});
const whitelabelName = computed(() => whitelabel.value?.name || '');

const handleSave = async () => {
  const values = formGetValues<WhitelabelAttributes>();
  delete (values as { attachment: any })['attachment'];
  const payload: SaveWhitelabelConfigMutationInput = {
    clientMutationId: formId,
    config: values,
  };
  const { result: newWhitelabel, errors } = await whitelabelStore.saveWhitelabel(payload);
  if (errors) {
    formsStore.SET_ERRORS({ requesterId: formId, errors });
    return;
  }
  if (!newWhitelabel) {
    return;
  }
  toast.success(`Whitelabel '${newWhitelabel.name}' updated successfully!`);
  router.push(parentRoute);
};

const confirmDelete = async () => {
  await whitelabelStore.deleteWhitelabel(whitelabel.value?.id);
  showDeleteConfirmation.value = false;
  router.push(parentRoute);
  toast.success(`Whitelabel '${whitelabel.value?.name}' has been deleted!`);
};
const onCancel = () => formsStore.RESET_FIELD_VALUES(formId);
const showDeleteConfirmation = ref(false);

const triggerDeleteConfirmation = () => {
  showDeleteConfirmation.value = true;
};

const closeModal = () => {
  showDeleteConfirmation.value = false;
};
</script>
